import { useState } from 'react';
import { Navigate } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { Banner } from '@/components/ui/Feedback';
import { ApiError } from '@/lib/api';
import { useAuth } from './AuthContext';

/** L-01 — Login. Panel kiri memuat ringkasan aplikasi seperti diminta PRD. */
export function LoginPage() {
  const { user, login, loading } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) {
    if (user.role === 'pelita' && user.individualId) {
      return <Navigate to={`/database/pelita/${user.individualId}`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(identifier, password, remember);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Terjadi kesalahan. Silakan coba lagi.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      {/* Panel brand — di mobile jadi header ringkas di atas */}
      <section className="flex flex-col justify-center gap-6 px-6 py-10 sm:px-12 lg:px-16">
        <div className="flex items-center gap-3">
          <img src="/favicon.png" alt="Logo LENTERA" className="size-11 object-contain" />
          <div>
            <p className="text-xl font-semibold text-ink">LENTERA</p>
            <p className="text-[13px] text-ink-secondary">Sekolah Literasi Indonesia</p>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="text-2xl leading-tight font-semibold text-ink sm:text-[28px]">
            Literasi untuk Menilai Transformasi dan Keberdayaan TBM
          </h1>
          <ul className="mt-5 flex flex-col gap-2.5 text-ink-secondary">
            <Bullet>Ukur indeks keberdayaan TBM secara periodik</Bullet>
            <Bullet>Nilai kompetensi individu PELITA SLI</Bullet>
            <Bullet>Dokumentasikan monev &amp; refleksi program</Bullet>
            <Bullet>Pantau perkembangan lintas periode dalam satu sistem</Bullet>
          </ul>
        </div>
      </section>

      {/* Form */}
      <section className="flex items-center justify-center px-6 pb-12 sm:px-12 lg:px-16">
        <div className="card w-full max-w-sm p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-ink">Masuk</h2>
          <p className="mt-1 text-sm text-ink-secondary">
            Gunakan akun yang diberikan admin SLI.
          </p>

          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            {error && <Banner tone="danger">{error}</Banner>}

            <Input
              label="Email atau Username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />

            <div className="relative">
              <Input
                label="Kata Sandi"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                className="absolute right-1.5 bottom-1 grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-app"
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>

            <label className="flex min-h-11 items-center gap-2.5 text-sm text-ink-secondary">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="size-4 accent-[var(--color-primary)]"
              />
              Ingat saya di perangkat ini
            </label>

            <Button type="submit" loading={submitting}>
              Masuk
            </Button>
          </form>

          <p className="mt-6 text-center text-[13px] text-ink-muted">v0.1 · SLI 2026</p>
        </div>
      </section>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
      {children}
    </li>
  );
}
